import { useCallback, useRef, useState } from "react";

/* Make a fixed-position panel draggable by a header handle. Returns a ref to
 * put on the panel, a mousedown handler for the drag handle, and a style to
 * spread onto the panel. Until the user drags, `dragStyle` is empty so the
 * panel keeps its default CSS position (e.g. docked top-right); the first drag
 * switches it to absolute left/top coordinates, clamped to the viewport.
 * Clicking a button/input inside the handle does NOT start a drag. */
export function useDraggable() {
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const drag = useRef<{ sx: number; sy: number; ox: number; oy: number } | null>(null);

  const onDragStart = useCallback((e: React.MouseEvent) => {
    // Don't hijack clicks on interactive controls in the handle (e.g. ✕).
    if ((e.target as HTMLElement).closest("button,input,textarea,a,select")) return;
    const el = panelRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    drag.current = { sx: e.clientX, sy: e.clientY, ox: r.left, oy: r.top };

    const onMove = (ev: MouseEvent) => {
      if (!drag.current || !el) return;
      const w = el.offsetWidth;
      const dx = ev.clientX - drag.current.sx;
      const dy = ev.clientY - drag.current.sy;
      let x = drag.current.ox + dx;
      let y = drag.current.oy + dy;
      // Keep it on-screen: never fully off, always leave the header grabbable.
      x = Math.min(Math.max(8 - w + 60, x), window.innerWidth - 60);
      y = Math.min(Math.max(0, y), window.innerHeight - 44);
      setPos({ x, y });
    };
    const onUp = () => {
      drag.current = null;
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    e.preventDefault();
  }, []);

  const dragStyle: React.CSSProperties = pos
    ? { left: pos.x, top: pos.y, right: "auto", bottom: "auto" }
    : {};

  return { panelRef, onDragStart, dragStyle };
}
